// SAB-1173: lets a LIST resolve active-news once and every ROW read the answer.
//
// The badge lives in row components (MVPCard, PlayerRow) that have no idea what else
// is on screen, so they cannot batch. Only the list owner can. Prop-drilling a Set
// through every intermediate component would mean changing signatures all over the
// legacy MLB UI; a context keeps the batching without that churn.
//
// ONE query per provider, never one per row — a per-row query on the draft board is
// the failure mode useActiveNewsPlayers exists to prevent.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useActiveNewsPlayers } from '@/hooks/useActiveNewsPlayers';

type Ctx = { active: Set<string>; ready: boolean } | null;
const ActiveNewsContext = createContext<Ctx>(null);

export function ActiveNewsProvider({
  sport, keys, children,
}: { sport: 'mlb' | 'nfl'; keys: string[]; children: ReactNode }) {
  const active = useActiveNewsPlayers(sport, keys);
  const value = useMemo(() => ({ active, ready: true }), [active]);
  return <ActiveNewsContext.Provider value={value}>{children}</ActiveNewsContext.Provider>;
}

/**
 * @returns true/false when a provider is present, `undefined` when there is none.
 *
 * `undefined` deliberately means "unknown", and PlayerNewsIcon treats it as the legacy
 * always-render behaviour. A surface that has not been wrapped keeps working exactly
 * as it did rather than silently losing its badge — the failure mode of a missing
 * provider should be the old behaviour, not an invisible feature.
 */
export function useIsNewsActive(key: string | null | undefined): boolean | undefined {
  const ctx = useContext(ActiveNewsContext);
  if (!ctx || !key) return undefined;
  return ctx.active.has(key);
}
